import React from 'react'
import {
	Box, Chip, Divider, Typography,
} from '@mui/material'
import { AppDialog, AppDialogHeader, AppDialogBody } from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { dsColorNew } from '@palette'
import { Schedule as ScheduleIcon, EmojiEvents as EmojiEventsIcon, CheckCircleOutline as CheckIcon, RadioButtonUnchecked as BlockIcon, PlayCircleOutline as PlayCircleIcon } from 'Pages/DsRepricing/@components/icons'
import { daysBetween, ruleHoursForDay, WEEKDAY_LABELS } from '../mockData'
import { figuresOf, headlineOf, fmt } from '../@services/runSummary'
import { tonePillSx, toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { displayDay } from '@utils'
import { format } from 'date-fns'

/**
 * Modal detail of a calendar cell — full rule frequency, hour list,
 * strategies applied vs not-applied and the reason each was rejected.
 */
export default function Popover({ open, onClose, dayIso, today, evaluation, ran, onOpenChanges }) {
	if (!dayIso || !evaluation) return null
	const date = new Date(dayIso + 'T00:00:00')
	const dba  = daysBetween(today, date)

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="md" paperSx={setupScopeSx}>
			<AppDialogHeader icon="calendar-day" onClose={onClose}
			                 title={`${format(date, 'EEEE')} ${displayDay(dayIso)}`}
			                 subtitle={dba === 0 ? 'Today' : dba > 0 ? `${dba} days from today · ${dba} DBA` : `${-dba} days ago`}/>
			<AppDialogBody sx={{ ...dialogBodySx, borderTop: `1px solid ${dsColorNew.grayBorder}`, pt: 2.5 }}>
				{dba < 0 && (<>
					<RanSection ran={ran} onOpenChanges={onOpenChanges}/>
					<Divider sx={{ my: 2.5 }}/>
				</>)}
				<Section title="Schedule rules" icon={<ScheduleIcon/>}
				         empty="No schedule rule applies to this day.">
					{evaluation.applicableRules.map(r => (
						<RuleBlock key={r.id} rule={r}/>
					))}
				</Section>

				<Divider sx={{ my: 2.5 }}/>

				<Section title="Applicable strategies" icon={<EmojiEventsIcon/>}
				         empty="No strategy applies to this day." asStrategyList>
					{evaluation.applicableStrategies.map(s => (
						<StratBlock key={s.id} strat={s}/>
					))}
				</Section>
			</AppDialogBody>
		</AppDialog>
	)
}

/** What ran on a past day, in full: the headline, then every figure
 *  that is not zero, with the push state spelled out. */
function RanSection({ ran, onOpenChanges }) {
	const headline = headlineOf(ran)
	const figures = figuresOf(ran)
	const push = ran?.push || {}
	return (
		<Box>
			<Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
				<Box sx={{ color: dsColorNew.textTitle, display: 'flex' }}>
					<PlayCircleIcon sx={{ fontSize: 22 }}/>
				</Box>
				<Typography sx={{ fontWeight: 700, fontSize: 14,
				                  textTransform: 'uppercase', letterSpacing: '0.05em' }}>
					What ran
				</Typography>
				{headline && (
					<Box component="span" sx={{ color: dsColorNew.textMuted,
					                            fontSize: 13, fontWeight: 600 }}>
						· {headline}
					</Box>
				)}
			</Box>
			{!headline ? (
				<Typography color={dsColorNew.textMuted} sx={{ fontSize: 13 }}>
					Nothing ran this day.
				</Typography>
			) : (<>
				<Box display="flex" flexWrap="wrap" gap={0.75} sx={{ mb: 1.25 }}>
					{figures.map(f => (
						<Chip key={f.key} size="small" label={`${fmt(f.n)} ${f.label}`}
						      title={f.query ? 'See these decisions' : undefined}
						      onClick={f.query && onOpenChanges ? () => onOpenChanges(f) : undefined}
						      sx={toneChipSx(f.tone, { clickable: !!f.query })}/>
					))}
				</Box>
				{push.searches > 0 && (
					<Typography color={dsColorNew.textMuted} sx={{ fontSize: 13 }}>
						PMS push: {fmt(push.flushed)} search{push.flushed === 1 ? '' : 'es'} sent
						{push.flushed_by_deadline ? `, ${fmt(push.flushed_by_deadline)} on deadline` : ''}
						{push.pending ? `, ${fmt(push.pending)} waiting` : ''}
						{push.failed ? `, ${fmt(push.failed)} given up` : ''}
						{push.superseded ? `, ${fmt(push.superseded)} superseded by a newer search` : ''}
						{push.calls_ok || push.calls_error
							? ` · ${fmt(push.calls_ok)} calls ok${push.calls_error ? `, ${fmt(push.calls_error)} refused` : ''}`
							: ''}
						. Days are UTC.
					</Typography>
				)}
			</>)}
		</Box>
	)
}

function Section({ title, icon, empty, children, asStrategyList }) {
	const kids = React.Children.toArray(children)
	return (
		<Box>
			<Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
				<Box sx={{ color: asStrategyList ? dsColorNew.successText : dsColorNew.appBlue,
				           display: 'flex' }}>
					{React.cloneElement(icon, { sx: { fontSize: 22 }})}
				</Box>
				<Typography sx={{ fontWeight: 700, fontSize: 14,
				                  textTransform: 'uppercase', letterSpacing: '0.05em' }}>
					{title}
				</Typography>
				<Box component="span" sx={{ color: dsColorNew.textMuted,
				                            fontSize: 13, fontWeight: 600 }}>
					· {kids.length}
				</Box>
			</Box>
			{kids.length ? kids : (
				<Typography color={dsColorNew.textMuted} sx={{ fontSize: 13 }}>
					{empty}
				</Typography>
			)}
		</Box>
	)
}

function SubHead({ children }) {
	return (
		<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
		                  textTransform: 'uppercase', letterSpacing: '0.05em',
		                  fontWeight: 700, mb: 1 }}>
			{children}
		</Typography>
	)
}

function RuleBlock({ rule }) {
	const hours = ruleHoursForDay(rule)
	const weekdayStr = rule.weekdays.map(w => WEEKDAY_LABELS[w]).join(' ')
	const freqStr = ruleFreqStr(rule)
	const scopeStr = rule.office_codes?.length
		? `Offices: ${rule.office_codes.join(', ')}`
		: 'All offices'
	return (
		<Box sx={{ mb: 2, borderRadius: '12px', overflow: 'hidden',
		           border: `1px solid ${dsColorNew.grayBorder}`,
		           bgcolor: dsColorNew.white }}>
			{/* Header — high-contrast primary */}
			<Box sx={{ px: 2, py: 1.25, bgcolor: dsColorNew.pillActiveBg,
			           color: dsColorNew.appBlue,
			           display: 'flex', alignItems: 'center', gap: 1 }}>
				<PriorityBadge value={rule.priority} onDark/>
				<Typography sx={{ fontWeight: 600, fontSize: 14,
				                  color: dsColorNew.textTitle }}>
					{rule.name}
				</Typography>
				<StatusChip live={rule.is_active}/>
				<Box flex={1}/>
				<Box sx={{ px: 1.25, py: 0.4, borderRadius: 1,
				           bgcolor: dsColorNew.white, color: dsColorNew.appBlue,
				           fontSize: 13, fontWeight: 700 }}>
					{hours.length}× today
				</Box>
			</Box>
			{/* Body */}
			<Box sx={{ p: 2, display: 'grid',
			           gridTemplateColumns: 'auto 1fr', gap: '10px 16px',
			           fontSize: 13 }}>
				<Row label="Frequency"><span>{freqStr}</span></Row>
				{rule.frequency_kind === 'every_n_hours' && (
					<Row label="Window"><span>{rule.window_start}–{rule.window_end}</span></Row>
				)}
				<Row label="Days"><span>{weekdayStr}</span></Row>
				<Row label="Scope"><span>{scopeStr}</span></Row>
				<Row label="Pickup">
					<HourOrAny value={rule.pickup_hour}/>
				</Row>
				<Row label="Dropoff">
					<HourOrAny value={rule.dropoff_hour}/>
				</Row>
				<Row label="Hours">
					<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
						{hours.map(h => (
							<Chip key={h} label={h} size="small" sx={toneChipSx('info')}/>
						))}
					</Box>
				</Row>
			</Box>
		</Box>
	)
}

function HourOrAny({ value }) {
	if (!value) {
		return (
			<Box component="span" sx={{ color: dsColorNew.textMuted,
			                            fontStyle: 'italic',
			                            fontSize: 13 }}>
				any time
			</Box>
		)
	}
	return (
		<Chip label={value} size="small" sx={toneChipSx('neutral')}/>
	)
}

function Row({ label, children }) {
	return (
		<React.Fragment>
			<Box sx={{ color: dsColorNew.textMuted, fontWeight: 700,
			           textTransform: 'uppercase', letterSpacing: '0.03em',
			           fontSize: 12 }}>
				{label}
			</Box>
			<Box sx={{ color: dsColorNew.textTitle, fontWeight: 500 }}>
				{children}
			</Box>
		</React.Fragment>
	)
}

function StatusChip({ live }) {
	return (
		<Box component="span" sx={{ ...tonePillSx(live ? 'ok' : 'neutral'), backgroundColor: dsColorNew.white }}>
			<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }}/>
			{live ? 'Live' : 'Paused'}
		</Box>
	)
}

function PriorityBadge({ value, onDark }) {
	return (
		<Box sx={{
			display:'inline-flex', width: 28, height: 28,
			bgcolor: onDark ? dsColorNew.white : dsColorNew.appBlue,
			color: onDark ? dsColorNew.appBlue : dsColorNew.white,
			borderRadius: '6px', alignItems:'center',
			justifyContent:'center', fontSize: 13,
			fontWeight: 700, flexShrink: 0 }}>
			{value ?? '—'}
		</Box>
	)
}

function StratBlock({ strat }) {
	const weekdayStr = strat.weekdays.map(w => WEEKDAY_LABELS[w]).join(' ')
	const season = strat.season_from && strat.season_to
		? `${strat.season_from} → ${strat.season_to}`
		: 'year-round'
	const scopeStr = scopeStrategyText(strat.office_codes, strat.sipp_codes)
	return (
		<Box sx={{ mb: 2, borderRadius: '12px', overflow: 'hidden',
		           border: `1px solid ${dsColorNew.grayBorder}`,
		           bgcolor: dsColorNew.white }}>
			<Box sx={{ px: 2, py: 1.25, bgcolor: dsColorNew.successBg,
			           color: dsColorNew.successText,
			           display: 'flex', alignItems: 'center', gap: 1 }}>
				<PriorityBadge value={strat.priority} onDark/>
				<Typography sx={{ fontWeight: 600, fontSize: 14,
				                  color: dsColorNew.textTitle }}>
					{strat.name}
				</Typography>
				<StatusChip live={strat.is_active}/>
			</Box>
			<Box sx={{ p: 2, display: 'grid',
			           gridTemplateColumns: 'auto 1fr', gap: '10px 16px',
			           fontSize: 13 }}>
				<Row label="Season"><span>{season}</span></Row>
				<Row label="Lead time"><span>DBA {strat.dba_from}–{strat.dba_to}</span></Row>
				<Row label="Days"><span>{weekdayStr}</span></Row>
				<Row label="Scope"><span>{scopeStr}</span></Row>
			</Box>
		</Box>
	)
}

function NoRow({ name, priority, reason, isPriority }) {
	return (
		<Box display="flex" alignItems="center" gap={0.85}
		     sx={{ py: 0.75, fontSize: 13 }}>
			<BlockIcon sx={{ fontSize: 18, color: dsColorNew.textSubtle }}/>
			{isPriority && priority != null && (
				<Box component="span" sx={{
					display:'inline-flex', width: 22, height: 22,
					bgcolor: dsColorNew.grayBorder, color: dsColorNew.textMuted,
					borderRadius: '4px', alignItems:'center',
					justifyContent:'center', fontSize: 12,
					fontWeight: 700 }}>
					{priority}
				</Box>
			)}
			<Box component="span" sx={{ fontWeight: 700, color: dsColorNew.textMuted }}>
				{name}
			</Box>
			<Box component="span" sx={{ color: dsColorNew.textSubtle }}>
				— {reason}
			</Box>
		</Box>
	)
}

function ruleFreqStr(rule) {
	if (rule.frequency_kind === 'every_n_hours')
		return `Every ${rule.frequency_value} h`
	if (rule.frequency_kind === 'specific_hours')
		return `Specific hours (${(rule.frequency_value || []).join(', ')})`
	return '—'
}

function scopeStrategyText(offices, sipps) {
	const parts = []
	parts.push(offices?.length ? `Offices: ${offices.join(', ')}` : 'All offices')
	parts.push(sipps?.length ? `ACRISS: ${sipps.join(', ')}` : 'All ACRISS')
	return parts.join(' · ')
}
